import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Default ON so the email pre-fills next visit (fixes "it forgot my id")
    const stored = localStorage.getItem('spacecal_remember_me');
    return stored === null ? true : stored === 'true';
  });
  const [formData, setFormData] = useState({
    email: localStorage.getItem('spacecal_saved_email') || '',
    password: '',
    name: ''
  });
  
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const normalizedEmail = (formData.email || '').trim().toLowerCase();
      const payload = isLogin 
        ? { email: normalizedEmail, password: formData.password }
        : { ...formData, email: normalizedEmail };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Save email if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('spacecal_saved_email', formData.email);
        localStorage.setItem('spacecal_remember_me', 'true');
      } else {
        localStorage.removeItem('spacecal_saved_email');
        localStorage.removeItem('spacecal_remember_me');
      }
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo.png" alt="SpaceCal logo" data-testid="auth-logo" className="w-12 h-12 rounded-2xl object-cover" />
            <span className="heading-font text-2xl font-bold">SpaceCal.ai</span>
          </div>
          <h1 className="text-3xl heading-font font-bold tracking-tight mb-2" data-testid="auth-title">
            {isLogin ? 'Welcome Back' : 'Start Your Free Trial'}
          </h1>
          <p className="text-gray-400 body-font">
            {isLogin ? 'Sign in to your account' : '7 days free, no credit card required'}
          </p>
        </div>

        <div className="dark-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="body-font text-gray-300">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="body-font text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="body-font text-gray-300">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-400 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-400 focus:ring-teal-400"
                data-testid="remember-me-checkbox"
              />
              <Label htmlFor="rememberMe" className="text-sm text-gray-400 cursor-pointer">
                Remember me on this device
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full teal-btn py-6 text-lg transition-snap"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-gray-400 hover:text-teal-400 transition-snap body-font"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;